import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useToast } from '@aslanpay/portal-common-lib';

import { SALARY_DISCREPANCIES_QUERY_KEY } from 'app/hooks/useFetchSalaryDiscrepancies';

import SalaryReconciliationsApiHandler from 'app/services/salaryReconciliationsApiHandler';

import {
  ResolveSalaryDiscrepancyRequest,
  SalaryReconciliationDiscrepancy,
} from 'app/types/salaryReconciliations';

const DEFAULT_ERROR_DESCRIPTION =
  'Sorry, the problem could not be marked as fixed. Please try again.';

const getErrorDescription = (error: Error) =>
  (axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message) ||
  DEFAULT_ERROR_DESCRIPTION;

/** Marks an open salary accrual discrepancy as fixed by hand, with a note saying why. */
const useResolveSalaryDiscrepancy = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<SalaryReconciliationDiscrepancy, Error, ResolveSalaryDiscrepancyRequest>(
    (request) => SalaryReconciliationsApiHandler.resolveDiscrepancy(request),
    {
      onSuccess: () => {
        toast({ position: 'top-right', status: 'success', title: 'Problem marked as fixed' });
      },
      onError: (error) => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: getErrorDescription(error),
        });
      },
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: [SALARY_DISCREPANCIES_QUERY_KEY] }),
    }
  );
};

export default useResolveSalaryDiscrepancy;
